class PillowQrVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-video]');
    if (!this.video) return;

    this._storeVariant1();

    // Set initial altitude display before playback begins
    const altEl = this.querySelector('[data-altitude]');
    const label = this.dataset.altitudeLabel || '';
    if (altEl && label.includes('{n}')) {
      altEl.textContent = label.replace('{n}', (30000).toLocaleString());
    }

    this._onTimeUpdate = () => this.updateMeta();
    this.video.addEventListener('timeupdate', this._onTimeUpdate);

    const wrap = this.querySelector('.pqv__video-wrap');
    if (wrap) {
      if (this.video.readyState >= 1) {
        this._applyAspectRatio(wrap);
      } else {
        this.video.addEventListener('loadedmetadata', () => this._applyAspectRatio(wrap), { once: true });
      }
    }

    this._initSwitcher();
  }

  _applyAspectRatio(wrap) {
    const video = this.video;
    if (!video.videoWidth || !video.videoHeight) return;
    wrap.style.aspectRatio = video.videoWidth + ' / ' + video.videoHeight;

    const ratio = video.videoWidth / video.videoHeight;
    const isPortrait = ratio < 1;
    const isDesktop = window.innerWidth >= 768;

    if (isPortrait && isDesktop) {
      // On desktop, a full-width portrait video is too tall — cap height at 85vh
      // and derive the corresponding max-width so layout stays within page bounds.
      // On mobile (< 768px) portrait videos fill the screen naturally — no constraint.
      const maxW = window.innerHeight * 0.85 * ratio;
      wrap.style.maxWidth = Math.round(maxW) + 'px';
      wrap.style.marginInline = 'auto';
    } else {
      // Landscape (any viewport) or portrait on mobile: fill container width normally
      wrap.style.maxWidth = '';
      wrap.style.marginInline = '';
    }
  }

  _storeVariant1() {
    const eyebrowEl = this.querySelector('.pqv__eyebrow');
    this._v1 = {
      sources: [...this.querySelectorAll('[data-video] source')].map(s => ({ src: s.src, type: s.type })),
      poster: this.video.getAttribute('poster') || '',
      heading: this.querySelector('.pqv__heading')?.textContent || '',
      eyebrow: eyebrowEl?.textContent || '',
      duration: this.querySelectorAll('.pqv__meta span')[2]?.textContent || '',
      captions: [...this.querySelectorAll('[data-video] track')].map(t => ({
        src: t.src, kind: t.kind, srclang: t.srclang, label: t.label
      }))
    };
  }

  _initSwitcher() {
    const buttons = this.querySelectorAll('[data-variant]');
    if (!buttons.length) return;

    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        this._switchVariant(btn.dataset.variant, buttons);
      });
    });
  }

  _switchVariant(variant, buttons) {
    buttons.forEach(b => {
      const active = b.dataset.variant === variant;
      b.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    const video = this.video;
    const headingEl = this.querySelector('.pqv__heading');
    const eyebrowEl = this.querySelector('.pqv__eyebrow');
    const timeEl = this.querySelector('[data-time-current]');

    video.pause();

    if (variant === '1') {
      this._setSources(video, this._v1.sources, this._v1.poster, this._v1.captions);
      if (headingEl) headingEl.textContent = this._v1.heading;
      if (eyebrowEl) eyebrowEl.textContent = this._v1.eyebrow;
      const durationSpan = this.querySelectorAll('.pqv__meta span')[2];
      if (durationSpan) durationSpan.textContent = this._v1.duration;
    } else {
      const urls = (this.dataset.variant2Sources || '').split('|').filter(Boolean);
      const types = (this.dataset.variant2Types || '').split('|').filter(Boolean);
      const sources = urls.map((url, i) => ({ src: url, type: types[i] || '' }));
      const poster = this.dataset.variant2Poster || '';
      const captions = this.dataset.variant2Captions
        ? [{ src: this.dataset.variant2Captions, kind: 'captions', srclang: 'en', label: 'English' }]
        : [];

      this._setSources(video, sources, poster, captions);

      const heading2 = this.dataset.variant2Heading || '';
      const duration2 = this.dataset.variant2Duration || '';

      if (headingEl) headingEl.textContent = heading2;
      if (eyebrowEl) {
        const base = this._v1.eyebrow.split('·')[0].trim();
        eyebrowEl.textContent = duration2 ? base + ' · ' + duration2 : base;
      }
      const durationSpan = this.querySelectorAll('.pqv__meta span')[2];
      if (durationSpan) durationSpan.textContent = duration2 || '—';
    }

    if (timeEl) timeEl.textContent = '0:00';
    video.currentTime = 0;
    video.load();

    const wrap = this.querySelector('.pqv__video-wrap');
    if (wrap) {
      video.addEventListener('loadedmetadata', () => this._applyAspectRatio(wrap), { once: true });
    }
  }

  _setSources(video, sources, poster, captions) {
    [...video.querySelectorAll('source, track')].forEach(el => el.remove());
    sources.forEach(({ src, type }) => {
      const s = document.createElement('source');
      s.src = src;
      if (type) s.type = type;
      video.appendChild(s);
    });
    captions.forEach(({ src, kind, srclang, label }) => {
      const t = document.createElement('track');
      t.src = src; t.kind = kind; t.srclang = srclang; t.label = label;
      video.appendChild(t);
    });
    video.poster = poster;
  }

  updateMeta() {
    if (!this.video.duration) return;

    const altEl = this.querySelector('[data-altitude]');
    if (altEl) {
      const remaining = 1 - (this.video.currentTime / this.video.duration);
      const alt = Math.round(remaining * 30000 / 100) * 100;
      const label = this.dataset.altitudeLabel || 'ALT {n} FT';
      altEl.textContent = label.replace('{n}', alt.toLocaleString());
    }

    const timeEl = this.querySelector('[data-time-current]');
    if (timeEl) timeEl.textContent = this.formatTime(this.video.currentTime);
  }

  formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return m + ':' + s;
  }

  disconnectedCallback() {
    this.video?.removeEventListener('timeupdate', this._onTimeUpdate);
  }
}

customElements.define('pillow-qr-video', PillowQrVideo);
